import { BOARD_CONFIG, type BoardType } from '@/(routers)/boards/editor/block/blockConfig';
import { registerWireBegin } from '@/(routers)/boards/editor/block/generators/arduino/_gen';

type Position = keyof (typeof BOARD_CONFIG)[BoardType]['pins'];
type AccelType = 'ABS' | 'X' | 'Y' | 'Z';
type AngleType = 'X' | 'Y' | 'Z';

let _accelUsed = false;
let _angleUsed = false;
let _accelPosition: Position = 'UPPER_LEFT';
const _usedAccelTypes = new Set<AccelType>();
const _usedAngleTypes = new Set<AngleType>();

export function accelUsed(): boolean {
    return _accelUsed;
}
export function angleUsed(): boolean {
    return _angleUsed;
}
export function markAccelUsed(position: Position, type: AccelType): void {
    _accelUsed = true;
    _accelPosition = position;
    _usedAccelTypes.add(type);
}
export function markAngleUsed(position: Position, type: AngleType): void {
    _angleUsed = true;
    _accelPosition = position;
    _usedAngleTypes.add(type);
}
export function resetAccelerometer(): void {
    _accelUsed = false;
    _angleUsed = false;
    _accelPosition = 'UPPER_LEFT';
    _usedAccelTypes.clear();
    _usedAngleTypes.clear();
}

export function accelerometerHelperCode(board: BoardType): string {
    if (!_accelUsed && !_angleUsed) return '';

    const wire = board === 'blue' ? 'Wire1' : 'Wire';

    const lines: string[] = [
        `#define MPU6050_ADDR 0x68`,
        `#define MPU6050_SMPLRT_DIV   0x19`,
        `#define MPU6050_CONFIG       0x1a`,
        `#define MPU6050_GYRO_CONFIG  0x1b`,
        `#define MPU6050_ACCEL_CONFIG 0x1c`,
        `#define MPU6050_PWR_MGMT_1   0x6b`,
        `int16_t raw_acc_x, raw_acc_y, raw_acc_z, raw_t, raw_gyro_x, raw_gyro_y, raw_gyro_z;`,
        `volatile float acc_x, acc_y, acc_z, acc_angle_x, acc_angle_y;`,
        `volatile float absAccelerometer;`,
        `double gyro_angle_x = 0, gyro_angle_y = 0, gyro_angle_z = 0;`,
        `float interval, preInterval;`,
        `double offsetX = 0, offsetY = 0, offsetZ = 0;`,
        `float angleX, angleY, angleZ;`,
        `float dpsX, dpsY, dpsZ;`,
        `double init_angleX = 0, init_angleY = 0, init_angleZ = 0;`,
        `volatile float rel_angleX, rel_angleY, rel_angleZ;`,
        ``,
        `void accelerometerWireRead() {`,
        `  ${wire}.beginTransmission(MPU6050_ADDR);`,
        `  ${wire}.write(0x3B);`,
        `  ${wire}.endTransmission(false);`,
        `  ${wire}.requestFrom(MPU6050_ADDR, 14, true);`,
        `  if (${wire}.available() >= 14) {`,
        `    raw_acc_x = ${wire}.read() << 8 | ${wire}.read();`,
        `    raw_acc_y = ${wire}.read() << 8 | ${wire}.read();`,
        `    raw_acc_z = ${wire}.read() << 8 | ${wire}.read();`,
        `    raw_t = ${wire}.read() << 8 | ${wire}.read();`,
        `    raw_gyro_x = ${wire}.read() << 8 | ${wire}.read();`,
        `    raw_gyro_y = ${wire}.read() << 8 | ${wire}.read();`,
        `    raw_gyro_z = ${wire}.read() << 8 | ${wire}.read();`,
        `  }`,
        `}`,
    ];

    if (_angleUsed) {
        lines.push(
            ``,
            `void writeMPU6050(byte reg, byte data) {`,
            `  ${wire}.beginTransmission(MPU6050_ADDR);`,
            `  ${wire}.write(reg);`,
            `  ${wire}.write(data);`,
            `  ${wire}.endTransmission();`,
            `}`,
        );
    }

    if (_accelUsed) {
        lines.push(
            ``,
            `void calcAcceleration() {`,
            `  acc_x = ((float)raw_acc_x) / 16384.0;`,
            `  acc_y = ((float)raw_acc_y) / 16384.0;`,
            `  acc_z = ((float)raw_acc_z) / 16384.0;`,
            `  absAccelerometer = sqrtf(acc_x * acc_x + acc_y * acc_y + acc_z * acc_z);`,
            `}`,
        );
    }

    if (_angleUsed) {
        lines.push(
            ``,
            `void calcRotation() {`,
            `  acc_x = ((float)raw_acc_x) / 16384.0;`,
            `  acc_y = ((float)raw_acc_y) / 16384.0;`,
            `  acc_z = ((float)raw_acc_z) / 16384.0;`,
            `  acc_angle_y = atan2(acc_x, acc_z + abs(acc_y)) * 360 / -2.0 / PI;`,
            `  acc_angle_x = atan2(acc_y, acc_z + abs(acc_x)) * 360 / 2.0 / PI;`,
            `  dpsX = ((float)raw_gyro_x) / 65.5;`,
            `  dpsY = ((float)raw_gyro_y) / 65.5;`,
            `  dpsZ = ((float)raw_gyro_z) / 65.5;`,
            `  interval = millis() - preInterval;`,
            `  preInterval = millis();`,
            `  gyro_angle_x += (dpsX - offsetX) * (interval * 0.001);`,
            `  gyro_angle_y += (dpsY - offsetY) * (interval * 0.001);`,
            `  gyro_angle_z += (dpsZ - offsetZ) * (interval * 0.001);`,
            `  angleX = (0.996 * gyro_angle_x) + (0.004 * acc_angle_x);`,
            `  angleY = (0.996 * gyro_angle_y) + (0.004 * acc_angle_y);`,
            `  angleZ = gyro_angle_z;`,
            `  gyro_angle_x = angleX;`,
            `  gyro_angle_y = angleY;`,
            `  gyro_angle_z = angleZ;`,
            `  rel_angleX = init_angleX - angleX;`,
            `  rel_angleY = init_angleY - angleY;`,
            `  rel_angleZ = init_angleZ - angleZ;`,
            `}`,
        );
    }

    if (board === 'blue') {
        lines.push(
            ``,
            `void _sbeSubProcess(void * pvParameters) {`,
            `  TickType_t lastWake = xTaskGetTickCount();`,
            `  while (1) {`,
            `    accelerometerWireRead();`,
            ...(_accelUsed ? [`    calcAcceleration();`] : []),
            ...(_angleUsed ? [`    calcRotation();`] : []),
            `    vTaskDelayUntil(&lastWake, pdMS_TO_TICKS(5));`,
            `  }`,
            `}`,
        );
    }

    if (_angleUsed) {
        lines.push(
            ``,
            `void accelerometerAngleSetup() {`,
            `  accelerometerWireRead();`,
            `  writeMPU6050(MPU6050_SMPLRT_DIV, 0x00);`,
            `  writeMPU6050(MPU6050_CONFIG, 0x00);`,
            `  writeMPU6050(MPU6050_GYRO_CONFIG, 0x08);`,
            `  writeMPU6050(MPU6050_ACCEL_CONFIG, 0x00);`,
            `  writeMPU6050(MPU6050_PWR_MGMT_1, 0x01);`,
            `  Serial.print("Calculate Calibration");`,
            `  for (int i = 0; i < 3000; i++) {`,
            `    accelerometerWireRead();`,
            `    dpsX = ((float)raw_gyro_x) / 65.5;`,
            `    dpsY = ((float)raw_gyro_y) / 65.5;`,
            `    dpsZ = ((float)raw_gyro_z) / 65.5;`,
            `    offsetX += dpsX;`,
            `    offsetY += dpsY;`,
            `    offsetZ += dpsZ;`,
            `    if (i % 1000 == 0) Serial.print(".");`,
            `  }`,
            `  Serial.println();`,
            `  offsetX /= 3000;`,
            `  offsetY /= 3000;`,
            `  offsetZ /= 3000;`,
            `  Serial.print("Calculate Rotation");`,
            `  for (int i = 0; i < 1000; i++) {`,
            `    calcRotation();`,
            `    if (i % 1000 == 0) Serial.print(".");`,
            `  }`,
            `  Serial.println();`,
            `  init_angleX = angleX;`,
            `  init_angleY = angleY;`,
            `  init_angleZ = angleZ;`,
            `}`,
        );
    }

    if (_accelUsed && board === 'orange') {
        lines.push(
            ``,
            `void accelerometerAccelerationSetup() {`,
            `  accelerometerWireRead();`,
            `  calcAcceleration();`,
            `}`,
        );
        if (_usedAccelTypes.has('ABS'))
            lines.push(
                ``,
                `float acceleration() {`,
                `  accelerometerWireRead();`,
                `  calcAcceleration();`,
                `  return absAccelerometer;`,
                `}`,
            );
        if (_usedAccelTypes.has('X'))
            lines.push(
                ``,
                `float accelerationX() {`,
                `  accelerometerWireRead();`,
                `  calcAcceleration();`,
                `  return acc_x;`,
                `}`,
            );
        if (_usedAccelTypes.has('Y'))
            lines.push(
                ``,
                `float accelerationY() {`,
                `  accelerometerWireRead();`,
                `  calcAcceleration();`,
                `  return acc_y;`,
                `}`,
            );
        if (_usedAccelTypes.has('Z'))
            lines.push(
                ``,
                `float accelerationZ() {`,
                `  accelerometerWireRead();`,
                `  calcAcceleration();`,
                `  return acc_z;`,
                `}`,
            );
    }

    if (_angleUsed && board === 'orange') {
        if (_usedAngleTypes.has('X'))
            lines.push(
                ``,
                `float relativeAngleX() {`,
                `  accelerometerWireRead();`,
                `  calcRotation();`,
                `  return rel_angleX;`,
                `}`,
            );
        if (_usedAngleTypes.has('Y'))
            lines.push(
                ``,
                `float relativeAngleY() {`,
                `  accelerometerWireRead();`,
                `  calcRotation();`,
                `  return rel_angleY;`,
                `}`,
            );
        if (_usedAngleTypes.has('Z'))
            lines.push(
                ``,
                `float relativeAngleZ() {`,
                `  accelerometerWireRead();`,
                `  calcRotation();`,
                `  return rel_angleZ;`,
                `}`,
            );
    }

    return lines.join('\n') + '\n';
}

export function accelerometerSetupCode(board: BoardType): string {
    if (!_accelUsed && !_angleUsed) return '';

    const pins = BOARD_CONFIG[board].pins[_accelPosition];
    const wire = board === 'blue' ? 'Wire1' : 'Wire';
    const wakeup = `  ${wire}.beginTransmission(0x68);\n  ${wire}.write(0x6B);\n  ${wire}.write(0x01);\n  ${wire}.endTransmission();`;

    const lines: string[] = [];
    if (board === 'blue') {
        lines.push(`  Wire1.begin(${pins.first}, ${pins.second});`);
    } else {
        registerWireBegin(`  Wire.begin();\n`);
    }
    lines.push(wakeup);

    if (_angleUsed) {
        lines.push(`  accelerometerAngleSetup();`);
    } else if (board === 'orange') {
        lines.push(`  accelerometerAccelerationSetup();`);
    }

    if (board === 'blue') {
        lines.push(`  xTaskCreatePinnedToCore(_sbeSubProcess, "_sbeSubProcess", 4096, NULL, 1, NULL, 0);`);
    }

    return lines.join('\n') + '\n';
}
